/** @format */
import { Injectable } from '@angular/core';
import { StateService } from '@uirouter/angular';
import {
    CourseUnit,
    CourseUnitSelection,
    CustomCourseUnitAttainment,
    CustomStudyDraft,
    EntityWithRule,
    OtmId,
} from 'common-typescript/types';
import { NGXLogger } from 'ngx-logger';
import { filter, map, switchMap, take } from 'rxjs';
import {
    AppErrorHandler,
    canApplyForCustomAttainments,
    CourseUnitEntityService,
    NewPlanSubstitutionService,
    PlanEntityService,
    UiOperation,
    UiOperationTarget,
    UiOperationType,
} from 'sis-components';
import { DialogActions } from '../common/service/attainment-student-modal-service';
import { StudentPlanUiActionsServiceImplementation } from '../common/service/student-plan-ui-actions-service-implementation.service';
import { PlanStructureData } from './plan-data.service';
import { selectSubstitutionDialogOpener } from './select-substitution-dialog/select-substitution-dialog.component';

/** One handler per UI operation type, so leaving one out is a compile error instead of an operation that silently does nothing. */
export type PlanNgUiOperationHandlers = {
    [T in UiOperationType]: (target: UiOperationTarget<T>, data: PlanStructureData) => void;
};

/** Handles the UI operations that the plan structure of the plan page sends. */
@Injectable()
export class PlanNgUiOperationHandlerService {
    private readonly selectSubstitutionDialogOpener = selectSubstitutionDialogOpener();

    private readonly handlers: PlanNgUiOperationHandlers = {
        [UiOperationType.OPEN_MODULE]: (module, data) => this.openModule(module, data),
        [UiOperationType.OPEN_CUSTOM_STUDY_DRAFT_INFO_MODAL]: (customStudyDraft, data) =>
            this.openCustomStudyDraftInfoModal(customStudyDraft, data),
        [UiOperationType.OPEN_CUSTOM_ATTAINMENT_INFO_MODAL]: customAttainment =>
            this.studentPlanUIActionsImplementationService.openCustomAttainmentInfoModalHandler(customAttainment),
        [UiOperationType.OPEN_COURSE_UNIT_INFO_MODAL]: (courseUnit, data) =>
            this.studentPlanUIActionsImplementationService.openCourseUnitInfoModalHandler(
                courseUnit,
                data.validatablePlan,
                data.matchingStudyRight,
                true,
            ),
        [UiOperationType.OPEN_MODULE_INFO_MODAL]: (module, data) =>
            this.studentPlanUIActionsImplementationService.openModuleInfoModalHandler(
                module,
                data.validatablePlan,
                data.matchingStudyRight,
                data.planValidationResult,
            ),
        [UiOperationType.OPEN_SELECT_COMPLETION_METHOD_MODAL]: (courseUnit, data) =>
            this.openCompletionMethodModal(courseUnit, courseUnit.id, data, false),
        [UiOperationType.OPEN_ATTAINMENT_DETAILS_MODAL]: (courseUnit, data) => this.openAttainmentDetailsModal(courseUnit, data),
        [UiOperationType.OPEN_CUSTOM_COURSE_UNIT_ATTAINMENT_DETAILS_MODAL]: customCourseUnitAttainment =>
            this.openCustomCourseUnitAttainmentDetailsModal(customCourseUnitAttainment),
        [UiOperationType.OPEN_SELECT_COMPLETION_METHOD_WITH_GRADE_RAISE_ATTEMPT_MODAL]: (courseUnit, data) =>
            this.openSelectCompletionMethodWithGradeRaiseAttemptModal(courseUnit, data),
        [UiOperationType.OPEN_SELECT_SUBSTITUTION_MODAL]: (courseUnit, data) => this.openSelectSubstitutionModal(courseUnit, data),
        // The plan page renders no rules, and only rules ask for the custom study draft creation modal; the plan structure edit modal
        // handles it. Only the old plan edit view sends CLOSE_AND_SAVE. Getting either here means that assumption no longer holds.
        [UiOperationType.OPEN_CUSTOM_STUDY_DRAFT_CREATION_MODAL]: () =>
            this.logUnexpectedOperation(UiOperationType.OPEN_CUSTOM_STUDY_DRAFT_CREATION_MODAL),
        [UiOperationType.CLOSE_AND_SAVE]: () => this.logUnexpectedOperation(UiOperationType.CLOSE_AND_SAVE),
    };

    constructor(
        private readonly studentPlanUIActionsImplementationService: StudentPlanUiActionsServiceImplementation,
        private readonly planEntityService: PlanEntityService,
        private readonly newPlanSubstitutionService: NewPlanSubstitutionService,
        private readonly courseUnitEntityService: CourseUnitEntityService,
        private readonly appErrorHandler: AppErrorHandler,
        private readonly stateService: StateService,
        private readonly logger: NGXLogger,
    ) {}

    handle(uiOperation: UiOperation, data: PlanStructureData): void {
        this.handleOperation(uiOperation, data);
    }

    openCustomAttainmentApplication(customStudyDraftId: string, planId: OtmId): void {
        this.stateService.go('student.logged-in.profile.applications.create-custom-attainment-application', {
            customStudyDraftId,
            planId,
        });
    }

    openCreatePriorLearningInclusionApplication(customStudyDraftId: string, planId: OtmId): void {
        this.stateService.go('student.logged-in.profile.applications.create-prior-learning-inclusion-application', {
            customStudyDraftId,
            planId,
        });
    }

    private handleOperation<T extends UiOperationType>(
        uiOperation: { uiOperationType: T; target: UiOperationTarget<T> },
        data: PlanStructureData,
    ): void {
        this.handlers[uiOperation.uiOperationType](uiOperation.target, data);
    }

    private logUnexpectedOperation(uiOperationType: UiOperationType): void {
        this.logger.error(`The plan page cannot handle the UI operation '${uiOperationType}'.`);
    }

    private openModule(module: EntityWithRule, data: PlanStructureData): void {
        const previouslyFocusedElement = document.activeElement as HTMLElement;
        this.studentPlanUIActionsImplementationService.openModuleHandler(
            module,
            data.validatablePlan,
            data.matchingStudyRight,
            data.validatablePlanEducation,
            data.planValidationResult,
            data.allStudentStudyRights,
            previouslyFocusedElement,
        );
    }

    private openCustomStudyDraftInfoModal(customStudyDraft: CustomStudyDraft, data: PlanStructureData): void {
        const studentId = data.validatablePlan.plan.userId;
        const canApply = canApplyForCustomAttainments(data.validatablePlan, data.validatablePlanEducation, data.matchingStudyRight);
        this.studentPlanUIActionsImplementationService
            .openCustomStudyDraftInfoModalHandler(customStudyDraft, studentId, {
                customAttainmentApplicationsDisabled: !canApply,
            })
            .result.then(({ operation }) => {
                const customStudyDraftId = customStudyDraft.id;
                const planId = data.validatablePlan.plan.id;

                if (operation === 'INCLUSION_APPLICATION') {
                    this.openCreatePriorLearningInclusionApplication(customStudyDraftId, planId);
                }
                if (operation === 'CUSTOM_ATTAINMENT_APPLICATION') {
                    this.openCustomAttainmentApplication(customStudyDraftId, planId);
                }
            })
            .catch(this.appErrorHandler.defaultErrorHandler);
    }

    private openSelectCompletionMethodWithGradeRaiseAttemptModal(plannedCourseUnit: CourseUnit, data: PlanStructureData): void {
        const courseUnitSelection = data.validatablePlan.getCourseUnitSelection(plannedCourseUnit.id) as CourseUnitSelection;
        if (courseUnitSelection?.gradeRaiseAttempt && courseUnitSelection.gradeRaiseAttempt.courseUnitId !== plannedCourseUnit.id) {
            this.getCourseUnit(courseUnitSelection?.gradeRaiseAttempt.courseUnitId).subscribe(gradeRaiseAttemptCourseUnit => {
                this.openCompletionMethodModal(gradeRaiseAttemptCourseUnit, plannedCourseUnit.id, data, true);
            });
        } else {
            this.openCompletionMethodModal(plannedCourseUnit, plannedCourseUnit.id, data, true);
        }
    }

    private openAttainmentDetailsModal(courseUnit: CourseUnit, data: PlanStructureData): void {
        const courseUnitAttainment = data.validatablePlan.getCourseUnitAttainment(courseUnit.id);
        const courseUnitSelection = data.validatablePlan.getCourseUnitSelection(courseUnit.id) as CourseUnitSelection;
        const previouslyFocusedElement = document.activeElement as HTMLElement;

        this.studentPlanUIActionsImplementationService
            .openOpenAttainmentDetailsModalHandler(courseUnitAttainment, courseUnitSelection?.gradeRaiseAttempt)
            .subscribe({
                next: ref => {
                    ref.closed.subscribe((result: DialogActions) => {
                        if (result?.openSelectCompletionMethodModal) {
                            previouslyFocusedElement?.focus(); // sometimes ng-bootstrap loses focus when opening two modals immediately one after another
                            this.openSelectCompletionMethodWithGradeRaiseAttemptModal(courseUnit, data);
                        }
                    });
                },
                error: () => {},
            });
    }

    private openCustomCourseUnitAttainmentDetailsModal(customCourseUnitAttainment: CustomCourseUnitAttainment): void {
        this.studentPlanUIActionsImplementationService
            .openOpenAttainmentDetailsModalHandler(customCourseUnitAttainment, null)
            .pipe(take(1), this.appErrorHandler.defaultErrorHandler())
            .subscribe();
    }

    private openCompletionMethodModal(
        courseUnit: CourseUnit,
        plannedCourseUnitId: OtmId,
        data: PlanStructureData,
        isGradeRaiseAttempt: boolean,
    ): void {
        this.studentPlanUIActionsImplementationService
            .openSelectCompletionMethodModalHandler(courseUnit, plannedCourseUnitId, data.validatablePlan, isGradeRaiseAttempt)
            .result.then(res => {
                this.planEntityService
                    .updateMyPlan(res.validatablePlan.plan)
                    .pipe(take(1), this.appErrorHandler.defaultErrorHandler())
                    .subscribe();
            })
            .catch(this.appErrorHandler.defaultErrorHandler);
    }

    private openSelectSubstitutionModal(courseUnit: CourseUnit, data: PlanStructureData): void {
        // Persists through PlanEntityService, not NewPlanSubstitutionService's own save, which uses the legacy js-data model.
        this.selectSubstitutionDialogOpener({ courseUnit, validatablePlan: data.validatablePlan })
            .afterClosed()
            .pipe(
                take(1),
                filter(result => !!result),
                map(result =>
                    result.type === 'remove'
                        ? this.newPlanSubstitutionService.computeRemoveSubstitution(courseUnit, data.validatablePlan.plan)
                        : this.newPlanSubstitutionService.computeSaveSubstitution(
                              courseUnit,
                              result.substitution,
                              data.validatablePlan.plan,
                              result.substituteCourseUnitsByGroupId,
                          ),
                ),
                switchMap(plan => this.planEntityService.updateMyPlan(plan)),
                this.appErrorHandler.defaultErrorHandler(),
            )
            .subscribe();
    }

    private getCourseUnit(courseUnitId: OtmId) {
        return this.courseUnitEntityService.getById(courseUnitId).pipe(take(1), this.appErrorHandler.defaultErrorHandler());
    }
}
